import {isUndefined} from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {RendererOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon, IconName} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {
  InteractiveListItem,
  InteractiveListItemPublicProps,
} from '../../../../../../libs/shared/ui/src/components/interactiveList/interactiveListItem';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {SearchToken, SuggestionSelectHandler} from '../../../../../../srcCommon/inboxes/search/searchHelpers';
import {ModifiersEnum} from '../../../../../../srcCommon/inboxes/search/searchTypes';
import {buildVisualRecipientFromMatch} from '../../../../../helpers/models/recipientHelpers';
import {RecipientContactMatchModel} from '../../../../../models/api/recipientMatchModel';
import {RecipientRolesEnum} from '../../../../../models/api/recipientModel';
import {TeammateModel} from '../../../../../models/api/teammateModel';
import {ContactAvatar} from '../../../../common/avatars/contactAvatar';
import {TeammateAvatar} from '../../../../common/avatars/teammateAvatar';
import {Skeleton} from '../../../../common/skeleton';

/*
 * Constants.
 */

export const SEARCH_DROPDOWN_DEBOUNCE_WAIT_MS = 250;

/*
 * Props.
 */

export interface InboxesSearchSuggestionsBaseProps {
  query?: string;
  activeToken: SearchToken;
  onSuggestionSelect: SuggestionSelectHandler;
}

export interface InboxesSearchSuggestionsResourceProps<T> {
  modifiers: ReadonlyArray<ModifiersEnum>;
  valueKey: keyof T;
  primaryKey: keyof T;
  secondaryKey?: keyof T;

  sectionTitle: ReactNode;
  renderItem?: RendererOf<T>;
  icon?: IconName;
  renderIcon?: RendererOf<T>;
}

export interface InboxesSearchSuggestionItemProps {
  value: string;
  primaryLabel: ReactNode;
  secondaryLabel?: ReactNode;
}

interface InboxesSuggestionsDropdownItemIconProps {
  iconName?: IconName;
  color?: string;
}

interface InboxesSuggestionsDropdownItemTeammateAvatarProps {
  teammate?: TeammateModel;
}

interface InboxesSuggestionsDropdownItemRecipientAvatarProps {
  recipient?: RecipientContactMatchModel;
}

/*
 * Styles.
 */

export const InboxesSuggestionsDropdownSectionWrapper = styled.div`
  &:not(:last-of-type) {
    border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
  }
`;

export const InboxesSuggestionsDropdownSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 7px 0 6px;
`;

export const InboxesSuggestionsDropdownSectionTitle = styled.div`
  display: flex;
  color: ${(p) => p.theme.greys.shade60};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
  line-height: ${fontSizes.large};
  padding: 8px 16px 4px 16px;
`;

const StyledInboxesSuggestionsDropdownItem = styled.div<{$isHighlighted: boolean}>`
  display: grid;
  grid-row-gap: 4px;
  grid-template-columns: 25px auto 1fr;
  grid-template-areas: 'icon primary-label secondary-label';
  overflow: hidden;
  align-items: center;
  padding: 7px 16px;
  ${(p) =>
    p.$isHighlighted &&
    css`
      background-color: ${p.theme.greys.shade10};
    `};
`;

const StyledInboxesSuggestionsDropdownItemIcon = styled.div`
  grid-area: icon;
  margin-right: 8px;
`;

export const InboxesSuggestionsDropdownItemPrimaryContent = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.normal};
  grid-area: primary-label;
  ${ellipsis()};
`;

export const InboxesSuggestionsDropdownItemSecondaryContent = styled.div`
  padding-left: 2px;
  grid-area: secondary-label;
  color: ${(p) => p.theme.greys.shade60};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  ${ellipsis()};
`;

const StyledInboxesSuggestionsDropdownTopResultItem = styled.div<{$isHighlighted: boolean}>`
  display: grid;
  grid-row-gap: 4px;
  grid-template-areas:
    'icon subject date'
    '. recipients recipients';
  grid-template-columns: 25px 1fr minmax(0, auto);
  overflow: hidden;
  align-items: center;
  padding: 10px 16px;
  ${(p) =>
    p.$isHighlighted &&
    css`
      background-color: ${p.theme.greys.shade10};
    `};
`;

export const InboxesSuggestionsDropdownTopResultSubject = styled.div`
  grid-area: subject;
  color: ${(p) => p.theme.greys.shade90};
  font-weight: ${fontWeights.semibold};
  ${ellipsis()};
`;

export const InboxesSuggestionsDropdownTopResultDate = styled.div`
  padding-left: 2px;
  grid-area: date;
  align-self: end;
  color: ${(p) => p.theme.greys.shade70};
  text-transform: uppercase;
  font-size: ${fontSizes.tiny};
  line-height: ${fontSizes.medium};
  ${ellipsis()};
`;

export const InboxesSuggestionsDropdownTopResultRecipients = styled.div`
  grid-area: recipients;
  color: ${(p) => p.theme.greys.shade60};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  ${ellipsis()};
`;

const StyledInboxesSuggestionsDropdownModifierItem = styled.div<{$isHighlighted: boolean}>`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-areas: 'primary-label secondary-label';
  margin: 5px 6px;
  padding: 2px 5px;
  border-radius: 3px;
  background-color: ${(p) => (p.$isHighlighted ? p.theme.greys.shade30 : p.theme.greys.shade10)};
`;

const StyledInboxesSuggestionsDropdownItemAvatar = styled.div`
  margin: 2px 8px 0 0;
`;

/*
 * Components.
 */

export const InboxesSuggestionsDropdownItem: FC<InteractiveListItemPublicProps> = (props) => (
  <InteractiveListItem {...props}>
    {({itemProps, isHighlighted}) => (
      <StyledInboxesSuggestionsDropdownItem {...itemProps} $isHighlighted={isHighlighted}>
        {props.children}
      </StyledInboxesSuggestionsDropdownItem>
    )}
  </InteractiveListItem>
);

export const InboxesSuggestionsDropdownItemIcon: FC<InboxesSuggestionsDropdownItemIconProps> = ({
  iconName,
  color,
}) => {
  const theme = useUserTheme();

  return (
    <StyledInboxesSuggestionsDropdownItemIcon>
      {iconName ? (
        <Icon name={iconName} color={color || theme.greys.shade70} />
      ) : (
        <Skeleton width="16px" height="16px" />
      )}
    </StyledInboxesSuggestionsDropdownItemIcon>
  );
};

export const InboxesSuggestionsDropdownTopResultItem: FC<InteractiveListItemPublicProps> = (props) => (
  <InteractiveListItem {...props}>
    {({itemProps, isHighlighted}) => (
      <StyledInboxesSuggestionsDropdownTopResultItem {...itemProps} $isHighlighted={isHighlighted}>
        {props.children}
      </StyledInboxesSuggestionsDropdownTopResultItem>
    )}
  </InteractiveListItem>
);

export const InboxesSuggestionsDropdownModifierItem: FC<InteractiveListItemPublicProps> = (props) => (
  <InteractiveListItem {...props}>
    {({itemProps, isHighlighted}) => (
      <StyledInboxesSuggestionsDropdownModifierItem {...itemProps} $isHighlighted={isHighlighted}>
        {props.children}
      </StyledInboxesSuggestionsDropdownModifierItem>
    )}
  </InteractiveListItem>
);

export const InboxesSuggestionsDropdownItemTeammateAvatar: FC<
  InboxesSuggestionsDropdownItemTeammateAvatarProps
> = ({teammate}) => {
  if (isUndefined(teammate)) {
    return null;
  }

  return (
    <StyledInboxesSuggestionsDropdownItemAvatar>
      <TeammateAvatar size={VisualSizesEnum.SMALL} teammate={teammate} />
    </StyledInboxesSuggestionsDropdownItemAvatar>
  );
};

export const InboxesSuggestionsDropdownItemRecipientAvatar: FC<
  InboxesSuggestionsDropdownItemRecipientAvatarProps
> = ({recipient}) => {
  if (isUndefined(recipient)) {
    return null;
  }

  const visualRecipient = buildVisualRecipientFromMatch('global', RecipientRolesEnum.FROM, recipient);
  return (
    <StyledInboxesSuggestionsDropdownItemAvatar>
      <ContactAvatar size={VisualSizesEnum.SMALL} visualContact={visualRecipient} />
    </StyledInboxesSuggestionsDropdownItemAvatar>
  );
};

/*
 * Helpers.
 */

export function renderResourceItem<
  T,
  P extends InboxesSearchSuggestionsBaseProps & InboxesSearchSuggestionsResourceProps<T>,
>(item: T & InboxesSearchSuggestionItemProps, props: P, modifierSuggestionType: ModifiersEnum | undefined) {
  const {onSuggestionSelect, renderItem, icon, renderIcon} = props;

  if (renderItem) {
    return (
      <InboxesSuggestionsDropdownItem
        onSelect={(e, key) =>
          onSuggestionSelect(item.value, key, {
            insertWhiteSpace: true,
            isModifierSuggestion: true,
            modifierSuggestionType,
          })
        }
        key={item.value}
      >
        {renderItem(item)}
      </InboxesSuggestionsDropdownItem>
    );
  }

  return (
    <InboxesSuggestionsDropdownItem
      onSelect={(e, key) =>
        onSuggestionSelect(item.value, key, {
          insertWhiteSpace: true,
          isModifierSuggestion: true,
          modifierSuggestionType,
        })
      }
      key={item.value}
    >
      {renderIcon ? renderIcon(item) : <InboxesSuggestionsDropdownItemIcon iconName={icon} />}
      <InboxesSuggestionsDropdownItemPrimaryContent>
        {item.primaryLabel}
      </InboxesSuggestionsDropdownItemPrimaryContent>
      {item.secondaryLabel && (
        <InboxesSuggestionsDropdownItemSecondaryContent>
          {item.secondaryLabel}
        </InboxesSuggestionsDropdownItemSecondaryContent>
      )}
    </InboxesSuggestionsDropdownItem>
  );
}
